import { Box, Button } from "@mui/material";
import Typography from "@mui/material/Typography";
import CollapsibleText from "app/components/CollapsibleText";
import InfoSection from "app/components/InfoSection";
import { theme } from "app/theme";
import CancellationIcon from "assets/icons/cancellation.svg";
import DarkLogo from "assets/icons/dark-logo.svg";
import FireIcon from "assets/icons/fire.svg";
import Loading from "assets/icons/loading.svg";
import LocationIcon from "assets/icons/location_icon.svg";
import dayjs, { Dayjs } from "dayjs";
import { useGetBusinessesByUserIdQuery } from "lib/features/business/businessService";
import checkoutServices, {
  CustomFieldInstance,
  CustomFieldValue,
  FareHarborBooking,
  Image,
  Payment,
  Referral
} from "lib/features/checkout/checkoutService";
import moment from "moment";
import { useEffect, useState } from "react";
import "react-phone-number-input/style.css";
import { Carousel } from "react-responsive-carousel";
import "react-responsive-carousel/lib/styles/carousel.min.css";
import { useLocation, useNavigate } from "react-router-dom";
import { fareharborCheckoutStyles as styles } from "styles/Checkout/fareharborCheckout.styles";
import { calculateFees } from "util/calculateFees";
import { settings } from "util/carouselSettings";
import { formatAddress } from "util/formatAddress";
import { currencyFormat } from "util/formatCurrency";
import { parseImageUrl } from "util/parseImageUrl";
import ListIcon from "../../../assets/icons/list.svg";
import AdditionalInformationFareharbor from "./AdditionalInformationFareharbor";
import CustomerInformationFareharbor from "./CustomerInformationFormFareharbor";
import CustomerTypeSelector from "./CustomerTypeSelector";
import DateAndTimeFareharbor from "./DateAndTimeFareharbor";
import StripePayment from "./StripeCheckoutModal";
import SuccessModal from "./SuccessModal";

interface FarehaborCheckoutProps {
  referral: Referral;
}

interface UserDisplayData {
  name: string;
  avatar?: string;
  isBusiness?: boolean;
}

const LoadingSplash = () => {
  return (
    <Box sx={styles.overlay}>
      <Box>
        <img src={Loading} alt="Loading" />
      </Box>
    </Box>
  );
};

export default function FarehaborCheckout({
  referral,
}: FarehaborCheckoutProps) {
  const navigate = useNavigate();
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const showBack = queryParams.get("showBack");

  const [isLoading, setIsLoading] = useState(false);
  const [showCheckout, setShowCheckout] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const [userDisplayData, setUserDisplayData] = useState<UserDisplayData | null>(null);

  // Fetch business data if user is a business (role_id === 4)
  const { data: businessData } = useGetBusinessesByUserIdQuery(
    { id: referral?.user?.id ?? null },
    { skip: !referral?.user?.id || referral?.user?.role_id !== 4 }
  );

  // Payment Fields
  const [phoneNumber, setPhoneNumber] = useState<string>("");
  const [email, setEmail] = useState("");
  const [name, setName] = useState("");
  const [bookingId, setBookingId] = useState<number | null>(null);

  const [customFields, setCustomFields] = useState<CustomFieldInstance[]>(
    referral.selected_farehabor_availability?.custom_field_instances || []
  );
  const [customFieldsPrototype, setCustomFieldsPrototype] = useState<
    CustomFieldInstance[]
  >(
    referral.selected_farehabor_availability.customer_type_rates[0]
      ?.custom_field_instances || []
  );

  const [selectedRate, setSelectedRate] = useState(0);
  const [totalCost, setTotalCost] = useState(
    referral.selected_farehabor_availability.customer_type_rates[0].total / 100
  );

  const [isCalendarVisible, setIsCalendarVisible] = useState<boolean>(false);

  const [selectedDate, setSelectedDate] = useState<Dayjs | null>(
    referral.selected_farehabor_availability.start_at
      ? dayjs(referral.selected_farehabor_availability.start_at)
      : null
  );

  const [selectedTime, setSelectedTime] = useState(
    moment
      .utc(referral.selected_farehabor_availability?.start_at)
      .local()
      .format("hh:mm A")
  );
  const [availableTimes, setAvailableTimes] = useState(
    referral?.item?.fareharbor_availabilities

      ?.filter(
        (f) =>
          moment(f?.start_at).format("YYYY-MM-DD") ===
          moment(referral?.selected_farehabor_availability?.start_at).format(
            "YYYY-MM-DD"
          )
      )
      .flatMap((availability) =>
        moment.utc(availability.start_at).local().format("hh:mm A")
      )
  );

  const handleBusinessShare = () => {
    navigate(
      `/business/${referral?.item?.business?.id}?referredBy=${referral.user.id}`
    );
  };

  const [quantities, setQuantities] = useState<{ [key: number]: number }>({});

  const filterTimesForDate = (date: Date) => {
    const times = referral?.item.fareharbor_availabilities
      ?.filter(
        (f) =>
          moment(f?.start_at).format("YYYY-MM-DD") ===
          moment(date).format("YYYY-MM-DD")
      )
      .flatMap((availability) =>
        moment.utc(availability?.start_at).local().format("hh:mm A")
      );
    setAvailableTimes(times);
  };

  const handleDateChange = (date: Dayjs | null) => {
    if (date) {
      setSelectedDate(dayjs(date));
      filterTimesForDate(date.toDate());
    }
    setIsCalendarVisible(false);
  };

  const handleRateChange = (rate: any, quantity: number) => {
    console.log("handleRateChange", rate, quantity);
    setSelectedRate(rate.customer_prototype.pk);
    let costToSet = (rate.total * quantity) / 100;
    customFieldsPrototype.forEach(
      (customFieldsPrototype: CustomFieldInstance) => {
        if (customFieldsPrototype.custom_field.type === "extended-option") {
          const valueAddOn =
            customFieldsPrototype.custom_field.extended_options.filter(
              (f) => f.pk === customFieldsPrototype.value_given
            );

          if (valueAddOn.length > 0) {
            console.log(valueAddOn[0].name, valueAddOn[0].total);
            costToSet = costToSet + valueAddOn[0].total / 100;
          }
        }
      }
    );
    customFields.forEach((customField: CustomFieldInstance) => {
      if (customField.custom_field.type === "extended-option") {
        const valueAddOn = customField.custom_field.extended_options.filter(
          (f) => f.pk === customField.value_given
        );
        if (valueAddOn.length > 0) {
          console.log(valueAddOn[0].name, valueAddOn[0].total);
          costToSet = costToSet + valueAddOn[0].total / 100;
        }
      }
    });
    console.log("rateChanged", costToSet);
    setTotalCost(costToSet);
  };

  function handleBookNowClick() {
    setShowCheckout(true);
  }

  const [intent, setIntent] = useState<string>("");

  async function handleCheckoutClick() {
    setIsLoading(true);
    const bookedField: CustomFieldValue[] = [];
    customFields.forEach((field: CustomFieldInstance) => {
      if (field.value_given !== undefined) {
        if (field.value_given === "yes") {
          let pushField: CustomFieldValue = {
            custom_field: field.custom_field.pk,
            value: true,
          };
          bookedField.push(pushField);
        }
        if (field.value_given === "no") {
          let pushField: CustomFieldValue = {
            custom_field: field.custom_field.pk,
            value: false,
          };
          bookedField.push(pushField);
        }
        if (field.value_given !== "yes" && field.value_given !== "no") {
          let pushField: CustomFieldValue = {
            custom_field: field.custom_field.pk,
            value: field.value_given,
          };
          bookedField.push(pushField);
        }
      }
    });
    if (selectedRate < 1) {
      setSelectedRate(
        referral.selected_farehabor_availability.customer_type_rates[0].pk
      );
    }
    let payment: Payment = {
      name: name,
      phone: phoneNumber,
      email: email,
    };

    const booking: FareHarborBooking = {
      custom_field_values: bookedField,
      customers: [
        {
          customer_type_rate: selectedRate,
          customer_type_value: quantities[selectedRate]
            ? quantities[selectedRate]
            : 1,
        },
      ],
      external_id: referral.id,
      note: "",
      voucher_number: "",
      payment_info: payment,
      payment_id: intent,
      total: totalCost + calculateFees(totalCost),
    };
    try {
      const result = await checkoutServices.createFareharborStripeSession(
        booking
      );
      if (result.data.success) {
        setBookingId(result.data.booking_id);
        return result?.data?.result.clientSecret;
      } else {
        console.log(result);
        window.alert(result.data.message);
        setIsLoading(false);
        return null;
      }
    } catch (err) {
      console.error(JSON.stringify(err));
      setIsLoading(false);
    }
  }

  async function handleGetStripeClientSecret() {
    try {
      const result = (await checkoutServices.getStripeToken(totalCost + calculateFees(totalCost))).data;
      if (!result.success) throw new Error(result.message);
      setIntent(result?.result?.id);
      return result?.result?.token;
    } catch (err: any) {
      alert(err.message);
      console.error(JSON.stringify(err));
    }
  }

  const setAdditionalOptionCost = async (value: number) => {
    let totalCostToSet = getBaseCost();
    console.log("totalCost", totalCostToSet);
    customFieldsPrototype.forEach(
      (customFieldsPrototype: CustomFieldInstance) => {
        if (customFieldsPrototype.custom_field.type === "extended-option") {
          const valueAddOn =
            customFieldsPrototype.custom_field.extended_options.filter(
              (f) => f.pk === customFieldsPrototype.value_given
            );

          if (valueAddOn.length > 0) {
            console.log(valueAddOn[0].name, valueAddOn[0].total);
            totalCostToSet = totalCostToSet + valueAddOn[0].total / 100;
          }
        }
      }
    );
    customFields.forEach((customField: CustomFieldInstance) => {
      if (customField.custom_field.type === "extended-option") {
        const valueAddOn = customField.custom_field.extended_options.filter(
          (f) => f.pk === customField.value_given
        );
        if (valueAddOn.length > 0) {
          console.log(valueAddOn[0].name, valueAddOn[0]);
          if (valueAddOn[0].modifier_type === "adjust") {
            totalCostToSet = valueAddOn[0].total / 100;
          } else {
            totalCostToSet = totalCostToSet + valueAddOn[0].total / 100;
          }
        }
      }
    });
    setTotalCost(totalCostToSet);
  };

  const getBaseCost = () => {
    let cost = 0;
    if (
      referral.item.fareharbor_availabilities[0]?.custom_field_instances !==
      undefined
    ) {
      console.log("GetBasePrice");

      let bC =
        referral.selected_farehabor_availability.custom_field_instances.filter(
          (f) =>
            f?.custom_field?.type === "extended-option" &&
            f?.custom_field?.is_required
        )[0]?.custom_field.extended_options[0].total;
      let ratePk = selectedRate;
      if (selectedRate === 0) {
        ratePk =
          referral.selected_farehabor_availability.customer_type_rates[0].pk;
      }

      const rate =
        referral.selected_farehabor_availability.customer_type_rates.filter(
          (r) => r.pk === ratePk
        )[0];
      console.log(rate);
      const minSize = rate.minimum_party_size ? rate.minimum_party_size : 1;
      setQuantities({ [selectedRate]: minSize });
      if (bC === undefined) {
        bC = rate.total;
        cost = bC / 100;
      } else {
        cost = (bC * minSize) / 100;
      }
      return cost;
    } else {
      console.log("else");
      cost =
        (referral.selected_farehabor_availability.customer_type_rates[0].total *
          referral.selected_farehabor_availability.customer_type_rates[0]
            .minimum_party_size) /
        100;
      return cost;
    }
  };

  const timeChanged = (targetTime: any) => {
    if (selectedDate) {
      const targetDateTime = dayjs(
        `${selectedDate.format("YYYY-MM-DD")} ${targetTime}`,
        "YYYY-MM-DD hh:mm A"
      );

      const newSelectedAv = referral.item.fareharbor_availabilities.filter(item => {
        const startAt = dayjs(item.start_at);
        return startAt.isSame(targetDateTime);
      });
      console.log(newSelectedAv[0]);
      const rates = newSelectedAv[0].customer_type_rates[0];
      referral.selected_farehabor_availability = {
        ...referral.selected_farehabor_availability,
        start_at: newSelectedAv[0].start_at,
        end_at: newSelectedAv[0].end_at,
        customer_type_rates: newSelectedAv[0].customer_type_rates,
        custom_field_instances: newSelectedAv[0].custom_field_instances,
      };
    }
    setSelectedTime(targetTime);
  };

  // Fetch and set user display data based on role_id
  useEffect(() => {
    const fetchUserDisplayData = async () => {
      if (referral?.user) {
        const isBusiness = referral.user.role_id === 4;
        
        if (isBusiness && businessData) {
          const businesses = Array.isArray(businessData) 
            ? businessData 
            : [businessData];
          
          if (businesses.length > 0) {
            const business = businesses[0];
            setUserDisplayData({
              name: business?.business || `${referral.user.first_name} ${referral.user.last_name}`,
              avatar: business?.businessImages?.[0]?.uri
                ? parseImageUrl(business.businessImages[0].uri)
                : referral.user.avatar 
                  ? parseImageUrl(referral.user.avatar)
                  : undefined,
              isBusiness: true
            });
          } else {
            setUserDisplayData({
              name: `${referral.user.first_name} ${referral.user.last_name}`,
              avatar: referral.user.avatar ? parseImageUrl(referral.user.avatar) : undefined,
              isBusiness: true
            });
          }
        } else {
          setUserDisplayData({
            name: `${referral.user.first_name} ${referral.user.last_name}`,
            avatar: referral.user.avatar ? parseImageUrl(referral.user.avatar) : undefined,
            isBusiness: false
          });
        }
      }
    };

    fetchUserDisplayData();
  }, [referral?.user, businessData]);

  useEffect(() => {
    if (
      referral.item.fareharbor_availabilities[0]?.custom_field_instances !==
      undefined
    ) {
      setSelectedRate(
        referral.selected_farehabor_availability.customer_type_rates[0].pk ?? 0
      );
    }

    const cost = getBaseCost();
    setTotalCost(cost);
  }, []);

  return (
    <>
      {isLoading && <LoadingSplash />}
      {!showSuccess && (
        <>
          {/* Top Container */}
          <Box sx={styles.header}>
            {showBack === "true" && (
              <img
                src={ListIcon}
                width={24}
                height={24}
                style={{
                  position: "absolute",
                  top: 15,
                  left: 20,
                  cursor: "pointer",
                }}
                onClick={() => navigate(-1)}
              />
            )}

            <Box sx={styles.businessName(showBack)}>
              <Typography
                onClick={handleBusinessShare}
                sx={styles.businessText(theme, showBack)}
              >
                {referral?.item?.business?.business}
              </Typography>
            </Box>

            <Typography
              onClick={handleBusinessShare}
              sx={styles.viewMoreText(theme)}
            >
              View More
            </Typography>
          </Box>

          <Carousel {...settings}>
            {referral?.item?.farehabor_item_details?.images.map(
              (image: Image, index: number) => (
                <div key={index} style={styles.carouselImageContainer as any}>
                  <img
                    src={image.image_cdn_url}
                    alt={`Item ${index + 1}`}
                    style={styles.carouselImage as any}
                  />
                  <Box sx={styles.locationTag(theme)}>
                    <img
                      src={LocationIcon}
                      alt="Location Icon"
                      style={styles.locationImage as any}
                    />
                    {formatAddress(
                      referral.item.business?.address1,
                      referral.item.business?.city,
                      referral.item.business?.state
                    )}
                  </Box>
                </div>
              )
            )}
          </Carousel>

          <Box sx={styles.detailsHeaderBox}>
            <Typography sx={styles.title(theme)}>
              {referral?.item?.farehabor_item_details?.name}
            </Typography>

            <Typography sx={styles.subtitle(theme)}>
              {referral?.item?.farehabor_item_details?.headline}
            </Typography>
          </Box>

          {/* Divider */}
          <Box sx={styles.divider}></Box>

          {/* Bottom Container */}
          <Box sx={styles.detailsBox}>
            <Box sx={styles.descriptionContainer}>
              <Typography sx={styles.descriptionHeader(theme)}>
                Description
              </Typography>
              <CollapsibleText
                text={referral?.item?.farehabor_item_details?.description}
                limit={150}
                textStyle={styles.collapsibleTextDesc}
                seeMoreStyle={styles.collapsibleSeeMoreDesc}
                isMarkdownText={true}
              />
            </Box>

            {/* Date Time Container */}
            <Box sx={styles.dateAndTimeContainer}>
              <DateAndTimeFareharbor
                itemType={referral?.item?.item_type}
                handleDateChange={handleDateChange}
                selectedDate={selectedDate}
                isCalendarVisible={isCalendarVisible}
                setIsCalendarVisible={setIsCalendarVisible}
                selectedTime={selectedTime}
                setSelectedTime={timeChanged}
                availableTimes={availableTimes}
                fareharborAvailabilities={
                  referral?.item?.fareharbor_availabilities
                }
              />

              {/* Customer Types Container */}
              <Box sx={styles.customerTypeContainer}>
                <Typography sx={styles.customerTypeHeader(theme)}>
                  Let's get started!
                </Typography>
                <CustomerTypeSelector
                  rates={
                    referral?.selected_farehabor_availability
                      ?.customer_type_rates
                  }
                  selectedRate={selectedRate}
                  quantities={quantities}
                  handleRateChange={handleRateChange}
                  setQuantities={setQuantities}
                />
              </Box>
            </Box>

            {/* Additional Information */}
            <Box sx={styles.additionalOptionsContainer}>
              <Typography sx={styles.additionalOptionsHeader(theme)}>
                Additional Options
              </Typography>
              <AdditionalInformationFareharbor
                customFields={customFieldsPrototype}
                setCustomFields={setCustomFieldsPrototype}
                setTotalCost={setAdditionalOptionCost}
              />

              <AdditionalInformationFareharbor
                customFields={customFields}
                setCustomFields={setCustomFields}
                setTotalCost={setAdditionalOptionCost}
              />
            </Box>

            {/* Booking Notes */}
            {referral?.item?.farehabor_item_details?.booking_notes && (
              <InfoSection
                title="Booking Notes"
                content={referral?.item?.farehabor_item_details?.booking_notes}
              />
            )}

            {/* Cancellation Policy */}
            {referral?.item?.farehabor_item_details?.cancellation_policy && (
              <Box sx={styles.cancellationPolicyContainer}>
                <Box sx={styles.cancellationHeader}>
                  <img
                    src={CancellationIcon}
                    alt="fire-icon"
                    style={styles.cancellationIcon}
                  />
                  <Typography sx={styles.cancellationPolicyTitle(theme)}>
                    Cancellation Policy
                  </Typography>
                </Box>

                <CollapsibleText
                  text={
                    referral?.item?.farehabor_item_details?.cancellation_policy
                  }
                  limit={150}
                  textStyle={styles.collapsibleTextCanc}
                  seeMoreStyle={styles.collapsibleSeeMoreCanc}
                  isMarkdownText={true}
                />
              </Box>
            )}

            {/* Customer Information */}
            <Box sx={styles.customerInformationContainer}>
              <Typography sx={styles.customerInformationHeader(theme)}>
                Customer Information
              </Typography>
              <Typography sx={styles.customerInformationSubHeader(theme)}>
                *Required fields
              </Typography>
              <CustomerInformationFareharbor
                customFields={customFields}
                setCustomFields={setCustomFields}
                setTotalCost={setTotalCost}
              />
            </Box>
          </Box>

          {/* Referred By Container - Updated with business logic */}
          {userDisplayData && (
            <Box sx={styles.outerMarginContainer}>
              <Typography sx={styles.referredByText(theme)}>
                Referred by
              </Typography>
              <Box sx={styles.referredByContainer}>
                <img
                  src={userDisplayData.avatar || "/default-avatar.png"}
                  alt={`${userDisplayData.name} avatar`}
                  style={styles.userAvatar as any}
                />
                <Typography sx={styles.userNameText(theme)}>
                  {userDisplayData.isBusiness 
                    ? userDisplayData.name
                    : `${referral?.user?.first_name} ${referral?.user?.last_name[0]}.`
                  }
                </Typography>
              </Box>
            </Box>
          )}

          <Box sx={styles.poweredByContainer}>
            <Typography sx={styles.poweredByText}>Powered by</Typography>
            <Box sx={styles.logoImageContainer}>
              <img src={DarkLogo} alt="logo" style={styles.logoImage} />
            </Box>
          </Box>

          {/* Bottom Sticky Button */}
          <Box component="footer" sx={styles.footer}>
            <Box sx={styles.footerInner}>
              {/* Price and Tax Section */}
              {!isNaN(totalCost) && totalCost > 0 && (
                <Box sx={styles.priceContainer}>
                  <Typography sx={styles.priceValue(theme)}>
                    {"$"} {totalCost}
                  </Typography>
                  <Typography sx={styles.priceFee(theme)}>
                    {`+ ${currencyFormat(
                      calculateFees(totalCost)
                    )} (Tax & Fees)`}
                  </Typography>
                </Box>
              )}

              <Button
                onClick={(e) => {
                  e.stopPropagation();
                  handleBookNowClick();
                }}
              >
                <Box sx={styles.bookNowButton}>
                  <img src={FireIcon} alt="fire-icon" style={styles.fireIcon} />
                  <Box>
                    <Typography sx={styles.bookNowText}>BOOK NOW</Typography>
                  </Box>
                </Box>
              </Button>
            </Box>
          </Box>
        </>
      )}
      {showCheckout && !showSuccess && (
        <>
          <StripePayment
            setShowCheckout={setShowCheckout}
            price={totalCost}
            currencyFormat={currencyFormat}
            handleGetStripeClientSecret={handleGetStripeClientSecret}
            handleCheckoutClick={handleCheckoutClick}
            setShowSuccess={setShowSuccess}
            setName={setName}
            setPhoneNumber={setPhoneNumber}
            setEmail={setEmail}
            name={name}
            phoneNumber={phoneNumber}
            email={email}
            setIsLoading={setIsLoading}
          />
        </>
      )}

      {showSuccess && (
        <SuccessModal
          referral={referral}
          price={totalCost}
          selectedDate={selectedDate}
          selectedTime={selectedTime}
          endTime={referral?.selected_farehabor_availability?.end_at}
        />
      )}
    </>
  );
}